/* import __COLOCATED_TEMPLATE__ from './edit-story-codes-modal.hbs'; */
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import LSGModalBase from "../lsg/lsg-modal-dialog-base";
import { closeModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { all, task } from 'ember-concurrency';

/**
 * @type {string}   constant for when we have a select item that is the default signifying on selection.
 */
const EDIT_STORY_CODES_MODAL__NOTHING_SELECTED = '- None -';

export default LSGModalBase.extend({
  /**
   * Register services
   */
  applicationCommandService : service('commands/application-commands'),
  costCodeCommandsService : service('commands/cost-code-commands'),
  storyCodeCommandsService: service('commands/story-code-commands'),
  userCommandService : service('commands.user-commands'),
  companyCommandService: service('commands/company-commands'),
  errorTracker: service('error-tracker'),
  session: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),
  codesNoneOptionText: EDIT_STORY_CODES_MODAL__NOTHING_SELECTED,

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @param {User}    Optional.  User to edit store codes on behalf of.  If null, currentUser is used.
     */
    actorUser: null,

    /**
     * @param {Application}   Optional. If not passed in, you should be setting isOnlyUpdateStoryCodes to true
     */
    application: null,

    /**
     * @param {Boolean}   Optional. If true, do not show None as an option for Cost Codes
     */
    costCodeRequired: false,

    /**
     * @param {Function=}     Optional function to do something after the story codes have been updated.
     *
     * @param {Ember.A<CostCode>}
     */
    onStoryCodesUpdated: () => {}
  },

  /**
   * @inheritDoc
   */
  stateDefaults: {
    /**
     * @type {Ember.A<CostCode>}    Cost codes for this company.
     */
    allCostCodes: () => A(),

    /**
     * @type {Ember.A<StoryCode>}    Story codes for this company.
     */
    allStoryCodes: () => A(),

    /**
     * @type {String}
     */
    updatedStoryCode: '',

    /**
     * @type {CostCode}
     */
    updatedCostCode: null,
  },

  actorUser: computed('model.actorUser', 'currentUser', function() {
    return this.model.actorUser ? this.model.actorUser : this.currentUser;
  }),

  userIsAdmin: computed('actorUser', function() {
    return this.actorUser.isAdmin || this.actorUser.isCompanyAdmin;
  }),

  storedCompany: computed('actorUser', function() {
    return this.store.peekRecord('company', this.actorUser.company.id);
  }),

  teamsInUse: computed('storedCompany', function() {
    return this.storedCompany.canUseCompanyTeams && this.storedCompany.companyTeams.length > 0;
  }),

  costCodeAllowNone: computed('model.costCodeRequired', function() {
    return !this.model.costCodeRequired;
  }),

  selectedCostCodeValue: computed('state.updatedCostCode.body', function() {
    return this.state.updatedCostCode?.body ?? EDIT_STORY_CODES_MODAL__NOTHING_SELECTED;
  }),

  /**
   * @type {Boolean}    True if user has any valid story or cost codes.
   */
  _hasAnyValidCodes: computed('initializeData.isRunning', '_validStoryCodes', '_validCostCodes', function() {
    if (this.initializeData.isRunning) {
      return true;
    }

    return this._validStoryCodes.length || this._validCostCodes.length;
  }),

  /**
   * @type {Ember.A<CostCode>}    All the valid cost codes
   */
  _validCostCodes: computed(
    'state.allCostCodes.[]',
    function() {
      return this.state.allCostCodes.filter((costCode) => {
        return !costCode.hidden;
      });
    }
  ),

  /**
   * @type {Ember.A<CostCode>}    All the valid story codes
   */
  _validStoryCodes: computed(
    'state.allStoryCodes.[]',
    function() {
      return this.state.allStoryCodes.filter((costCode) => {
        return !costCode.hidden;
      });
    }
  ),

  initializeData: task(function *() {
    const application = this.model.application;
    this.set('state.updatedStoryCode', application?.storyCode ? application.storyCode : null);
    this.set('state.updatedCostCode', application?.costCode ? application.costCode : null);

    const pending = [this.loadCostCodes.perform(this.storedCompany)];

    if (this.storedCompany.canUseCompanyTeams) {
      pending.push(this.userCommandService.getUser(this.actorUser.id, ['company-teams']));
      pending.push(this.companyCommandService.loadCompany(this.storedCompany.id, ['company-teams']));
    }

    try {
      return yield all(pending);
    } catch (error) {
      handleErrorResponseWithModal(this, error, "There was a problem loading company codes. Please try again or contact support.");
    }
  }),

  loadCostCodes: task(function *(company) {
    try {
      const includes = company.canUseCompanyTeams ? ['company-teams'] : [];
      const costCodes = yield this.costCodeCommandsService.getCostCodesForCompany(company, includes);
      this.set('state.allCostCodes', costCodes);

      const storyCodes = yield this.storyCodeCommandsService.getStoryCodesForCompany(company, includes);
      this.set('state.allStoryCodes', storyCodes);

      if (this.model.isOnlyUpdateStoryCodes) {
        this.set('state.isInAvailableCostCodesMode', true);
      }
    } catch(error) {
      this.errorTracker.error(`loadCostCodes - Failed to load cost codes for company id ${company.id}`, this, error);
      throw error;
    }
  }),

  actions: {
    setCostCode(value) {
      this.set('state.updatedCostCode', value ? value : null);
    },

    setStoryCode(value) {
      this.set('state.updatedStoryCode', value ? value.body : null);
    },

    /**
     * Called when user clicks to update the cost codes.
     */
    onUpdateStoryCodesClicked: function() {
      let storyCode = this.state.updatedStoryCode;
      let costCode = this.state.updatedCostCode;

      const { application, onStoryCodesUpdated} = this.model;
      closeModal(this);

      if (application) {
        this.applicationCommandService.updateApplication(application, {
          'story_code' : storyCode,
          'cost_code' : costCode?.id ?? null,
        }).then(() => {
          application.set('storyCode', storyCode);
          application.set('costCode', costCode);
          onStoryCodesUpdated();
        });
      }
    },

    closeModal() {
      closeModal(this);
    },
  }
});
